import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { clearIdleMarker } from '../hooks/useIdleTimeout';
import { Lock, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { BRAND } from '../lib/brand';
import {
  PASSWORD_RULES, PASSWORD_TIP, PASSWORD_BREACH_MESSAGE,
  validateNewPassword, passwordStrength, passwordErrorMessage, isPasswordBreached,
} from '../lib/password';

export default function ResetPasswordPage() {
  const { clearPasswordReset, passwordResetReason } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    const invalid = validateNewPassword(password, confirm);
    if (invalid) {
      setError(invalid);
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (await isPasswordBreached(password)) {
        setError(PASSWORD_BREACH_MESSAGE);
        return;
      }
      const { error } = await Promise.race([
        supabase.auth.updateUser({ password }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Please go back to the login page and use the Forgot password link to request a new reset email.')), 15000)
        ),
      ]);
      if (error) throw error;
      // This is a new session as far as the idle clock is concerned; a marker
      // left over from before the reset would sign them out on arrival.
      clearIdleMarker();
      setDone(true);
      setTimeout(() => clearPasswordReset(), 1500);
    } catch (err) {
      setError(passwordErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const strength = passwordStrength(password);
  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.logoMark}>
              <img src={BRAND.logo} alt={BRAND.name} style={styles.logoImg} />
            </div>
            <h1 style={styles.title}>{BRAND.name}</h1>
            <p style={styles.subtitle}>Set your new password</p>
          </div>

          <div style={styles.body}>
            {done ? (
              <div style={styles.successBox}>
                Password updated. Taking you in now…
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {passwordResetReason === 'weak' && (
                  <div style={styles.notice}>
                    <ShieldAlert size={18} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                    <div>
                      Your password is correct, but it has appeared in a known data breach elsewhere on
                      the internet. Please set a new one before carrying on.
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label" htmlFor="new-password">New password</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={16} style={styles.inputIcon} />
                    <input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      style={{ paddingLeft: '2.4rem', paddingRight: '2.75rem' }}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter a new password"
                      required
                      autoComplete="new-password"
                      aria-describedby="password-rules"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      style={styles.eyeBtn}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p id="password-rules" style={styles.hint}>
                    {PASSWORD_RULES} {PASSWORD_TIP}
                  </p>
                  {strength && (
                    <p style={{ ...styles.hint, color: `var(--${strength.tone})`, fontWeight: 500 }} aria-live="polite">
                      {strength.label}{strength.hint ? `: ${strength.hint}` : ''}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="confirm-password">Confirm password</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={16} style={styles.inputIcon} />
                    <input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      style={{ paddingLeft: '2.4rem' }}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="Repeat your password"
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  {mismatch && (
                    <p style={{ ...styles.hint, color: 'var(--warning)' }} aria-live="polite">
                      Both boxes need to match.
                    </p>
                  )}
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={styles.submitBtn}
                  disabled={loading}
                >
                  {loading ? <span className="spinner" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }} /> : null}
                  {loading ? 'Updating…' : 'Set new password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--cream)',
    padding: '1rem',
  },
  container: {
    width: '100%',
    maxWidth: '420px',
  },
  card: {
    background: 'var(--white)',
    borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid rgba(var(--navy-rgb),0.08)',
    overflow: 'hidden',
  },
  header: {
    background: 'var(--navy)',
    padding: '2rem 2rem 1.5rem',
    textAlign: 'center',
  },
  logoMark: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '0.75rem',
  },
  logoImg: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid rgba(var(--terracotta-rgb),0.5)',
  },
  title: {
    color: 'var(--cream)',
    fontFamily: 'var(--font-serif)',
    fontSize: '1.6rem',
    fontWeight: 400,
    margin: 0,
  },
  subtitle: {
    color: 'rgba(var(--cream-rgb),0.7)',
    fontSize: '0.875rem',
    marginTop: '0.4rem',
  },
  body: {
    padding: '1.75rem',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '0.75rem',
    color: 'var(--navy)',
    opacity: 0.5,
    pointerEvents: 'none',
  },
  eyeBtn: {
    position: 'absolute',
    right: '0.75rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--navy)',
    opacity: 0.5,
    display: 'flex',
    alignItems: 'center',
    padding: '0.25rem',
  },
  hint: {
    fontSize: '0.8rem',
    color: 'var(--charcoal)',
    opacity: 0.75,
    margin: '0.4rem 0 0',
    lineHeight: 1.45,
  },
  notice: {
    display: 'flex',
    gap: '0.6rem',
    background: 'var(--warning-bg)',
    color: 'var(--charcoal)',
    borderRadius: 'var(--radius-md)',
    padding: '0.8rem 0.9rem',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    marginBottom: '1.25rem',
  },
  error: {
    background: 'var(--danger-bg)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-sm)',
    padding: '0.65rem 0.9rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  successBox: {
    background: 'var(--success-bg)',
    color: 'var(--success)',
    borderRadius: 'var(--radius-md)',
    padding: '1rem',
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  submitBtn: {
    width: '100%',
    justifyContent: 'center',
    padding: '0.8rem',
    fontSize: '1rem',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
};
